import type { OrderSortField, OrderStatus } from '@/gql/graphql';
import { paymentMethodLabel } from '@/lib/labels';
import { decimal, int, isoDate, oneOf, text, uuidList } from '@/hooks/useUrlState';

/** Every `OrderStatus` in the order the filter lists them; the `Record` makes the compiler flag a status added to the schema but not here. */
export const ORDER_STATUS_SET: Record<OrderStatus, true> = {
  NEW: true,
  OPEN: true,
  PENDING: true,
  CANCELING: true,
  FULL_PAID: true,
  PARTIAL_PAID: true,
  CANCELED: true,
  LOSS: true,
  FAILED: true,
  REFUNDING: true,
  REFUNDED: true,
  PARTIAL_REFUNDED: true,
  FULL_REFUNDED: true,
};

export type OrderMethod = 'pin' | 'cash' | 'tab' | 'online' | 'paypal' | 'stager';
/** Every method the filter offers, with its label: the Select options and the `method` codec both read this. */
export const ORDER_METHOD_LABELS: Record<OrderMethod, string> = {
  pin: 'PIN',
  cash: 'Cash',
  tab: 'Tab',
  online: 'Online',
  paypal: 'PayPal',
  stager: paymentMethodLabel('stager'),
};

/** The Select options of the two filters, in the order of the sets above, with "any" first. */
export const ORDER_STATUS_OPTIONS = [
  { value: '', label: 'Any status' },
  ...(Object.keys(ORDER_STATUS_SET) as OrderStatus[]).map((value) => ({ value, label: value.replace('_', ' ').toLowerCase() })),
];
export const ORDER_METHOD_OPTIONS = [
  { value: '', label: 'Any method' },
  ...(Object.entries(ORDER_METHOD_LABELS) as [OrderMethod, string][]).map(([value, label]) => ({ value, label })),
];

export type OrdersSortColumn = 'id' | 'status' | 'method' | 'device' | 'table' | 'discount' | 'total' | 'due' | 'createdAt';

/** DataGrid column `id` → GraphQL `OrderSortField` for server-side sort; its keys are the values `sort` accepts. */
export const ORDER_COLUMN_SORT: Record<OrdersSortColumn, OrderSortField> = {
  id: 'ID',
  status: 'STATUS',
  method: 'PAYMENT_METHOD',
  device: 'DEVICE_NAME',
  table: 'TABLE_NAME',
  discount: 'DISCOUNT_NAME',
  total: 'GRAND_TOTAL',
  due: 'AMOUNT_DUE',
  createdAt: 'CREATED_AT',
};

export const ORDERS_DEFAULT_SORT: { sort: OrdersSortColumn; dir: 'asc' | 'desc' } = { sort: 'createdAt', dir: 'desc' };

/** The /orders query string, in this order (see README "List pages, URL state"). */
export const ordersUrlSchema = {
  search: text(),
  status: oneOf(ORDER_STATUS_SET, ''),
  method: oneOf(ORDER_METHOD_LABELS, ''),
  from: isoDate(),
  to: isoDate(),
  totalMin: decimal(),
  totalMax: decimal(),
  dueMin: decimal(),
  dueMax: decimal(),
  devices: uuidList(),
  tables: uuidList(),
  discounts: uuidList(),
  products: uuidList(),
  sort: oneOf(ORDER_COLUMN_SORT, ORDERS_DEFAULT_SORT.sort),
  dir: oneOf<'asc' | 'desc', 'asc' | 'desc'>({ asc: true, desc: true }, ORDERS_DEFAULT_SORT.dir),
  page: int({ min: 1 }, 1),
  size: int({ min: 1, max: 500 }, 50),
};

/** Parse-only inbound link: the Discounts page's "Usage" link used to be `/orders?discountId=<id>`. */
export const ORDERS_URL_ALIASES = { discountId: 'discounts' };

/** The keys that live in the collapsible Filters panel: the panel opens on mount when any is set. */
export const ORDERS_ADVANCED_KEYS = [
  'status',
  'method',
  'from',
  'to',
  'totalMin',
  'totalMax',
  'dueMin',
  'dueMax',
  'devices',
  'tables',
  'discounts',
  'products',
] as const satisfies readonly (keyof typeof ordersUrlSchema)[];
